(ns app.front.view.property.v2.data-section
  (:require ["@carbon/icons-react/es/locked/24" :default Locked24]
            [app.domain.property.model :as property-model]
            [app.domain.util :as du]
            [app.front.ui :as ui]
            [app.front.ui.i18n :as ui-i18n]
            [app.front.ui.section :as section]
            [app.front.ui.select :as ui-select]
            [app.front.ui.table :as ui-table]
            [app.front.util :as futil]
            [app.front.view.property.subs :as property-subs]
            [app.front.view.property.v2.data-header :as data-header]
            [app.i18n.model :as i18n-model]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.util.string :as str-util]
            [core.util.util :as util]
            [potpuri.core :as p]
            [reagent.core :as r]))

(defn gis-protected?
  "Returns true if the attribute is GIS-linked and manual overwrite is not allowed"
  [{:property-attribute-template/keys [gis-attribute allow-gis-overwrite?]}]
  (boolean (and gis-attribute (not allow-gis-overwrite?))))

(defn number-input
  [{{:db/keys [id] :property-attribute-template/keys [type] :as template} :template
    :keys                                                                 [attribute lang property-id]}]
  (let [attr      (if (= type :property-attribute-template.type/integer)
                    :property-attribute/value-long
                    :property-attribute/value-decimal)
        step      (if (= type :property-attribute-template.type/integer)
                    1
                    0.1)
        on-change (fn [e]
                    (let [v (cond-> (futil/tval e)
                              true str-util/blank->nil
                              (= type :property-attribute-template.type/integer) futil/parse-int)]
                      (>evt [::property-subs/set-attribute-value-debounce property-id id attr v])))]
    [ui/number-input {:id                 (str "number-input-" id)
                      :label-text         (property-model/attribute-template-name template lang)
                      :hide-label         true
                      :wrapper-class-name "long"
                      :default-value      (get attribute attr)
                      :step               step
                      :disabled           (gis-protected? template)
                      :on-change          on-change}]))

(defn text-input
  [{{:db/keys [id] :as template}                 :template
    {:property-attribute/keys [value-localized]} :attribute
    :keys                                        [lang property-id]}]
  (r/with-let [*strings (r/atom (i18n-model/strings-map value-localized))]
    [:div.flex.gap-4
     [ui-i18n/localized-string-edit
      {:id                  (str "text-input-" id)
       :label-text          (property-model/attribute-template-name template lang)
       :hide-label          true
       :light               false
       :disabled            (gis-protected? template)
       :on-change           (fn [v]
                              (reset! *strings v)
                              (>evt [::property-subs/set-attribute-value-debounce
                                     property-id id :property-attribute/value-localized v]))
       :allowed-locales-sub ::property-subs/group-locales
       :strings             @*strings}]
     [ui-i18n/edit-language-selector
      {:allowed-locales-sub ::property-subs/group-locales
       :hide-label          true}]]))

(defn single-date-picker
  [{{:db/keys [id] :as template} :template
    :keys                        [lang property-id tz attribute value-attr width min-date invalid?]}]
  [ui/date-picker
   (p/assoc-if {:id         (str "date-picker-" id)
                :callback   (fn [d]
                              ;; Save the date at 0:00 in the group's timezone
                              (let [start-of-day (when (inst? d)
                                                   (date-util/date-at-time-in-tz d 0 0 tz))]
                                (>evt [::property-subs/set-attribute-value
                                       property-id id value-attr start-of-day])))
                :value      (some-> (get attribute value-attr)
                                    ;; Change the date from group timezone to browser timezone for display
                                    (date-util/to-tz tz))
                :label-text (property-model/attribute-template-name template lang)
                :hide-label true
                :disabled?  (gis-protected? template)}
               :width width
               :min-date min-date
               :invalid? invalid?)])

(defn date-range-picker
  [{{:property-attribute/keys [value-start-date value-end-date]} :attribute
    :as                                                          ctx}]
  [:div.flex.flex-wrap.gap-2.items-center
   [single-date-picker (assoc ctx
                              :value-attr :property-attribute/value-start-date
                              :width "200px")]
   [:div " - "]
   [single-date-picker (assoc ctx
                              :value-attr :property-attribute/value-end-date
                              :width "200px"
                              :min-date value-start-date
                              :invalid? (> value-start-date value-end-date))]])

(defmulti value-editor (fn [{{:property-attribute-template/keys [type]} :template}] type))

(defmethod value-editor :default [{{:property-attribute-template/keys [type]} :template}]
  [:div "No editor for attribute type " type])

(defmethod value-editor :property-attribute-template.type/select
  [{{:db/keys [id] :property-attribute-template/keys [options] :as template} :template
    {:property-attribute/keys [value-localized]}                             :attribute
    :keys                                                                    [lang property-id]}]
  (let [value      (some-> value-localized i18n-model/strings-map)
        avail-vals (->> options
                        (sort-by :ordered/order)
                        (map #(-> % :property-attribute-template-option/values i18n-model/strings-map)))
        values     (if (or (not value)
                           (some #(= % value) avail-vals))
                     avail-vals
                     (concat [value] avail-vals))]
    [ui-select/select {:id         (str "select-" id)
                       :values     values
                       :val->str   #(i18n-model/get-text-from-entities-or-map % lang)
                       :value      value
                       :label-text (property-model/attribute-template-name template lang)
                       :hide-label true
                       :class-name "full-width-select"
                       :disabled   (gis-protected? template)
                       :on-change  #(>evt [::property-subs/set-attribute-value
                                           property-id id :property-attribute/value-localized %])}]))

(defmethod value-editor :property-attribute-template.type/toggle
  [{{:db/keys                          [id]
     :property-attribute-template/keys [toggle-true-labels toggle-false-labels]
     :as                               template}                                :template
    {:property-attribute/keys [value-boolean]}                                  :attribute
    :keys                                                                       [lang property-id tr]}]
  [:div.-mt-4
   (cc/toggle {:toggled    (boolean value-boolean)
               :id         (str "toggle-" id)
               :class-name "m-0"
               :aria-label (property-model/attribute-template-name template lang)
               :disabled   (gis-protected? template)
               :labelA     (or (i18n-model/get-text-from-entities-or-map toggle-false-labels lang)
                               (tr [:property-attribute-template-toggle-default/false]))
               :labelB     (or (i18n-model/get-text-from-entities-or-map toggle-true-labels lang)
                               (tr [:property-attribute-template-toggle-default/true]))
               :on-toggle  #(>evt [::property-subs/set-attribute-value
                                   property-id id :property-attribute/value-boolean %])})])

(defmethod value-editor :property-attribute-template.type/integer [ctx]
  [number-input ctx])

(defmethod value-editor :property-attribute-template.type/decimal [ctx]
  [number-input ctx])

(defmethod value-editor :property-attribute-template.type/text [ctx]
  [text-input ctx])

(defmethod value-editor :property-attribute-template.type/date [ctx]
  [single-date-picker (assoc ctx :value-attr :property-attribute/value-date)])

(defmethod value-editor :property-attribute-template.type/date-range [ctx]
  [date-range-picker ctx])

(defn render-attr-value [{:keys [template attribute tr lang format-date property-id tz]}]
  (when (du/in-state? :property-attribute-template.state/enabled template)
    [:div
     (value-editor (util/mmap template attribute tr lang format-date property-id tz))]))

(defn attribute-table [{:keys [tr property]}]
  (let [editing?             (<sub [::property-subs/editing?])
        templates            (if editing?
                               (<sub [::property-subs/available-attribute-templates])
                               (<sub [::property-subs/current-attribute-templates]))
        lang                 (<sub [:language])
        template-id-attr-map (<sub [::property-subs/attribute-template-id-attribute-map])
        format-date          (<sub [::date-util/date-fn])
        tz                   (<sub [::property-subs/group-tz])]
    [:div
     [ui-table/modern-responsive-table
      {:tr                tr
       :id                "property-attributes-table"
       :items             templates
       :id-func           :db/id
       :get-status        #(when (:property-attribute-template/critical? %)
                             :attention)
       :empty-placeholder (tr [:property-view/no-attributes-set])
       :cols              [[:property-attribute-template/names
                            #(property-model/attribute-template-name % lang)]
                           [:property-attribute-template/critical?
                            #(when (:property-attribute-template/critical? %)
                               (tr [:property-attribute-template/critical?]))
                            {:width "200px"}]
                           [""
                            (fn [template]
                              (when (and editing?
                                         (gis-protected? template))
                                (r/create-element Locked24 #js{:aria-label (tr [:error/gis-overwrite-not-allowed])})))
                            {:width "40px"}]
                           [:property-attribute-template-option/values
                            (fn [template]
                              (let [attribute (get template-id-attr-map (:db/id template))]
                                (cond
                                  editing? [render-attr-value
                                            (util/mmap {:property-id (:db/id property)}
                                                       template attribute tr lang format-date tz)]

                                  attribute (property-model/attribute-value attribute lang tr format-date))))]]}]]))



(defn section [{:keys [tr property]}]
  (let [visible?                (<sub [::property-subs/data-section-visible?])
        extended-property-data? (<sub [::ff/enabled? :feature-flag.id/extended-property-data])]
    (when visible?
      [section/section
       :property-data
       {:tr tr}
       :property-view/data-title
       [:div
        (if extended-property-data?
          [data-header/data-header
           {:tr       tr
            :property property
            :content  [attribute-table (util/mmap tr property)]}]
          [attribute-table (util/mmap tr property)])]])))
